import { useEffect, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, MapPin, Lightbulb, Landmark, Building2, BookOpen, Loader2 } from "lucide-react";
import { COMMUNITY_PROFILES, CommunityProfile, getCommunityProfile } from "@/data/communityProfiles";

const CommunitiesIndex = () => {
  useEffect(() => {
    document.title = "Alabama Communities | emrkt.co";
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main className="pt-32 pb-20">
        <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-primary mb-2">
            <MapPin className="h-3.5 w-3.5" /> Alabama Archive
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold mb-2">Communities</h1>
          <p className="text-sm text-muted-foreground max-w-2xl mb-8">
            Narrative profiles for the Alabama places we're working in. Each profile ties local
            history and aspirations to the live ideas, projects, legislation, and partner
            organizations on the platform.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {COMMUNITY_PROFILES.map((c) => (
              <Link
                key={c.slug}
                to={`/communities/${c.slug}`}
                className="group bg-card border border-border rounded-xl p-6 hover:border-primary/40 transition-colors"
              >
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-muted-foreground mb-2">
                  <Badge variant="outline" className="text-[10px] capitalize">{c.kind}</Badge>
                  {c.parent && <span>· {c.parent}</span>}
                </div>
                <h2 className="font-display text-xl font-semibold mb-1 group-hover:text-primary transition-colors">
                  {c.name}
                </h2>
                <p className="text-sm text-muted-foreground">{c.tagline}</p>
              </Link>
            ))}
          </div>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
};

const CommunityProfileView = ({ profile }: { profile: CommunityProfile }) => {
  useEffect(() => {
    document.title = `${profile.name} | Community Profile | emrkt.co`;
  }, [profile]);

  const kwLower = useMemo(
    () => profile.match.keywords.map((k) => k.toLowerCase()),
    [profile],
  );
  const counties = profile.match.counties;

  const { data: ideas = [], isLoading: ideasLoading } = useQuery({
    queryKey: ["community-ideas", profile.slug],
    queryFn: async () => {
      const orFilter = kwLower
        .map((k) => `title.ilike.%${k}%,description.ilike.%${k}%`)
        .join(",");
      const { data, error } = await supabase
        .from("ideas")
        .select("id,title,description,image_url,category,vote_count,created_at")
        .eq("status", "published")
        .or(orFilter)
        .order("created_at", { ascending: false })
        .limit(12);
      if (error) throw error;
      return data ?? [];
    },
  });

  const { data: legislation = [], isLoading: legLoading } = useQuery({
    queryKey: ["community-legislation", profile.slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("legislation")
        .select("id,bill_id,title,description,status,county,date")
        .in("county", counties)
        .order("date", { ascending: false, nullsFirst: false })
        .limit(10);
      if (error) throw error;
      return data ?? [];
    },
  });

  const { data: partners = [], isLoading: partnersLoading } = useQuery({
    queryKey: ["community-partners", profile.slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("affiliates")
        .select("id,slug,name,tagline,blurb,logo_url,counties,tier,category")
        .eq("status", "active")
        .eq("is_published", true)
        .overlaps("counties", counties)
        .order("created_at", { ascending: false })
        .limit(12);
      if (error) throw error;
      return data ?? [];
    },
  });

  const { data: caseStudies = [], isLoading: caseLoading } = useQuery({
    queryKey: ["community-case-studies", profile.slug],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_public_case_studies");
      if (error) throw error;
      const all = (data ?? []) as any[];
      return all.filter((c) => {
        const hay = `${c.idea_title ?? ""} ${c.idea_description ?? ""} ${c.latest_summary ?? ""}`.toLowerCase();
        return kwLower.some((k) => hay.includes(k));
      });
    },
  });

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main className="pt-32 pb-20">
        <div className="container mx-auto px-4 sm:px-6 max-w-5xl">
          <Link
            to="/communities"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
          >
            <ArrowLeft className="h-4 w-4" /> All communities
          </Link>

          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-primary mb-2">
            <MapPin className="h-3.5 w-3.5" /> Alabama Archive · Community
          </div>
          <h1 className="font-display text-4xl font-bold mb-2">{profile.name}</h1>
          {profile.parent && (
            <p className="text-sm text-muted-foreground mb-2">In {profile.parent}</p>
          )}
          <p className="text-base text-foreground/90 max-w-3xl mb-6">{profile.tagline}</p>

          {/* Quick facts */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-8">
            {profile.quick_facts.map((f) => (
              <div key={f.label} className="bg-card border border-border rounded-lg p-3">
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
                  {f.label}
                </div>
                <div className="text-sm font-semibold text-foreground">{f.value}</div>
              </div>
            ))}
          </div>

          {/* Narrative */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
            <Section title="Overview" body={profile.overview} />
            <Section title="History" body={profile.history} />
            <Section title="Culture" body={profile.culture} />
          </div>

          <div className="bg-card border border-border rounded-xl p-6 mb-10">
            <h2 className="font-display text-lg font-semibold mb-3">Aspirations</h2>
            <ul className="space-y-2 text-sm">
              {profile.aspirations.map((a, i) => (
                <li key={i} className="flex gap-2">
                  <span className="text-primary mt-1">›</span>
                  <span className="text-foreground/90">{a}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Active ideas */}
          <ListBlock
            title="Active ideas & projects"
            Icon={Lightbulb}
            loading={ideasLoading}
            empty="No published ideas mention this community yet — be the first."
            items={ideas.map((i: any) => ({
              key: i.id,
              href: `/ideas/${i.id}`,
              title: i.title,
              subtitle: i.category ?? "Idea",
              detail: `▲ ${i.vote_count ?? 0}`,
            }))}
          />

          {/* Case studies */}
          <ListBlock
            title="Case studies"
            Icon={BookOpen}
            loading={caseLoading}
            empty="No public case studies for this community yet."
            items={(caseStudies as any[]).map((c) => ({
              key: c.project_id,
              href: `/archive/case-studies/${c.project_id}`,
              title: c.idea_title,
              subtitle: c.latest_outcome,
              detail: `${c.public_lessons_count} lesson${Number(c.public_lessons_count) === 1 ? "" : "s"}`,
            }))}
          />

          {/* Legislation */}
          <ListBlock
            title="Legislation in scope"
            Icon={Landmark}
            loading={legLoading}
            empty="No tracked legislation tagged to this county yet."
            items={(legislation as any[]).map((l) => ({
              key: l.id,
              href: "/legislation",
              title: l.title,
              subtitle: l.bill_id ?? l.status ?? "Bill",
              detail: l.date ? new Date(l.date).toLocaleDateString() : "",
            }))}
          />

          {/* Partners */}
          <ListBlock
            title="Partner organizations"
            Icon={Building2}
            loading={partnersLoading}
            empty="No published partners cover this community yet."
            items={(partners as any[]).map((p) => ({
              key: p.id,
              href: `/affiliates/${p.slug}`,
              title: p.name,
              subtitle: p.tier ?? p.category ?? "Partner",
              detail: (p.counties ?? []).slice(0, 2).join(", "),
            }))}
          />
        </div>
      </main>
      <SiteFooter />
    </div>
  );
};

const Section = ({ title, body }: { title: string; body: string }) => (
  <div className="bg-card border border-border rounded-xl p-5">
    <h3 className="font-display text-sm font-semibold mb-2 uppercase tracking-wide text-muted-foreground">
      {title}
    </h3>
    <p className="text-sm text-foreground/90 whitespace-pre-wrap leading-relaxed">{body}</p>
  </div>
);

type ListItem = {
  key: string;
  href: string;
  title: string;
  subtitle?: string;
  detail?: string;
};

const ListBlock = ({
  title,
  Icon,
  loading,
  empty,
  items,
}: {
  title: string;
  Icon: typeof Lightbulb;
  loading: boolean;
  empty: string;
  items: ListItem[];
}) => (
  <section className="mb-8">
    <div className="flex items-center gap-2 mb-3">
      <Icon className="h-5 w-5 text-primary" />
      <h2 className="font-display text-lg font-semibold">{title}</h2>
      {!loading && <span className="text-xs text-muted-foreground">({items.length})</span>}
    </div>
    {loading ? (
      <div className="flex justify-center py-6">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    ) : items.length === 0 ? (
      <div className="text-sm text-muted-foreground border border-dashed border-border rounded-lg p-5 text-center">
        {empty}
      </div>
    ) : (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {items.map((it) => (
          <Link
            key={it.key}
            to={it.href}
            className="group bg-card border border-border rounded-lg p-4 hover:border-primary/40 transition-colors"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-display font-semibold text-sm group-hover:text-primary transition-colors truncate">
                  {it.title}
                </div>
                {it.subtitle && (
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground mt-0.5 capitalize">
                    {it.subtitle}
                  </div>
                )}
              </div>
              {it.detail && (
                <span className="text-[11px] text-muted-foreground shrink-0">{it.detail}</span>
              )}
            </div>
          </Link>
        ))}
      </div>
    )}
  </section>
);

const Communities = () => {
  const { slug } = useParams<{ slug: string }>();
  if (!slug) return <CommunitiesIndex />;
  const profile = getCommunityProfile(slug);
  if (!profile) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <SiteHeader />
        <main className="pt-32 pb-20 container mx-auto px-6 max-w-2xl text-center">
          <h1 className="font-display text-2xl font-bold mb-2">Community profile not found</h1>
          <p className="text-sm text-muted-foreground mb-6">
            We're rolling community profiles out one place at a time, starting with Mobile County
            and Prichard.
          </p>
          <Link to="/communities" className="text-primary hover:underline text-sm">
            See available profiles →
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }
  return <CommunityProfileView profile={profile} />;
};

export default Communities;